import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { ChevronLeft } from "lucide-react";

export default function PublicTeamDetail() {
  const { id } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [allTeams, setAllTeams] = useState({});
  const [allTournaments, setAllTournaments] = useState({});

  useEffect(() => {
    Promise.all([api.get(`/teams/${id}`), api.get(`/players?team_id=${id}`), api.get("/games"), api.get("/teams"), api.get("/tournaments")])
      .then(([t, p, g, a, tt]) => {
        setTeam(t.data);
        setPlayers(p.data);
        setGames(g.data.filter((x) => x.team_a_id === id || x.team_b_id === id));
        setAllTeams(Object.fromEntries(a.data.map((x) => [x.id, x])));
        setAllTournaments(Object.fromEntries(tt.data.map((x) => [x.id, x])));
      });
  }, [id]);

  if (!team) return <div className="max-w-7xl mx-auto p-12 text-ink-muted">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <Link to="/teams" className="inline-flex items-center gap-1 text-ink-muted hover:text-orange text-xs uppercase tracking-widest font-heading mb-4">
        <ChevronLeft size={14} /> All teams
      </Link>

      <div className="flex items-center gap-6 mb-10">
        <div className="w-24 h-24 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden">
          {team.logo_url ? <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
            : <span className="font-heading font-black text-5xl text-orange">{team.name?.[0]}</span>}
        </div>
        <div>
          <div className="text-orange font-heading uppercase tracking-[0.3em] text-xs">Team</div>
          <h1 className="font-heading font-black text-5xl uppercase tracking-tight text-white">{team.name}</h1>
          {(team.tournament_ids && team.tournament_ids.length > 1) && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="team-tournaments-badges">
              {team.tournament_ids.map((tid) => (
                <span key={tid} className="inline-block px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest bg-orange/15 text-orange border border-orange/40 rounded-sm">
                  {allTournaments[tid]?.name || tid.slice(0, 6)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <section>
          <h2 className="font-heading uppercase tracking-widest text-orange text-sm mb-3">Roster</h2>
          <div className="border border-navy-border rounded-sm overflow-hidden divide-y divide-navy-border">
            {players.length === 0 ? <div className="p-6 text-center text-ink-muted text-sm">No players yet.</div>
              : players.map((p) => (
                <Link key={p.id} to={`/players/${p.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-navy-surface/40 transition-colors">
                  <span className="font-mono tabular text-orange font-black text-2xl w-10">{p.jersey_number}</span>
                  <div className="w-10 h-10 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
                    {p.photo_url ? <img src={fileUrl(p.photo_url)} alt="" className="w-full h-full object-cover" />
                      : <span className="font-heading font-bold text-white text-sm">{p.full_name?.[0]}</span>}
                  </div>
                  <span className="font-heading uppercase tracking-tight text-white">{p.full_name}</span>
                </Link>
              ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading uppercase tracking-widest text-orange text-sm mb-3">Game history</h2>
          <div className="border border-navy-border rounded-sm overflow-hidden divide-y divide-navy-border">
            {games.length === 0 ? <div className="p-6 text-center text-ink-muted text-sm">No games yet.</div>
              : games.map((g) => {
                const opp = g.team_a_id === id ? allTeams[g.team_b_id] : allTeams[g.team_a_id];
                const us = g.team_a_id === id ? g.score_a : g.score_b;
                const them = g.team_a_id === id ? g.score_b : g.score_a;
                const win = g.status === "finished" && us > them;
                return (
                  <Link key={g.id} to={`/games/${g.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-navy-surface/40">
                    <span className="text-xs font-mono text-ink-muted w-20 shrink-0">{g.date}</span>
                    <span className="flex-1 font-heading uppercase tracking-tight text-white truncate">vs {opp?.name || "TBD"}</span>
                    <span className={`font-mono tabular ${win ? "text-orange font-bold" : "text-ink-secondary"}`}>{us}–{them}</span>
                    <span className={`text-[10px] font-heading uppercase tracking-widest px-2 py-0.5 rounded-sm ${g.status === "finished" ? "bg-navy-surface text-ink-secondary" : g.status === "live" ? "bg-court-red text-white" : "bg-transparent border border-navy-border text-ink-muted"}`}>{g.status}</span>
                  </Link>
                );
              })}
          </div>
        </section>
      </div>
    </div>
  );
}
